import React, { useState, useEffect, useCallback } from 'react';
import { Box, Upload, X, Download, Trash2, Edit2, Search, Filter, Eye, HardDrive, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, Input, Select, InputGroup, ConfirmationModal, toast, ErrorBoundary } from '../../components/v2/V2Components';
import Model3DViewer from '../../components/v2/Model3DViewer';
import Model3DService from '../../services/Model3DService';
import HouseService from '../../services/HouseService';
import RoomService from '../../services/RoomService';
import { useBlobUrl } from '../../hooks/useBlobUrl';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const CATEGORY_LABELS = {
    furniture: 'Furniture',
    decoration: 'Decoration',
    fixture: 'Fixture',
    architectural: 'Architectural',
    appliance: 'Appliance',
    other: 'Other'
};

const FORMAT_COLORS = {
    glb: 'bg-blue-100 text-blue-700',
    gltf: 'bg-blue-50 text-blue-600',
    obj: 'bg-gray-100 text-gray-700',
    fbx: 'bg-orange-100 text-orange-700',
    usdz: 'bg-purple-100 text-purple-700',
    splat: 'bg-green-100 text-green-700',
    ply: 'bg-teal-100 text-teal-700'
};

const Models3D = () => {
    usePageView(ANALYTICS_EVENTS.MODELS_3D_VIEWED);
    const [models, setModels] = useState([]);
    const [houses, setHouses] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showViewerModal, setShowViewerModal] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);

    // Blob URL hook
    const { url: viewerUrl, loading: viewerLoading, loadUrl, setUrl } = useBlobUrl();

    // Upload form state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadName, setUploadName] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadCategory, setUploadCategory] = useState('other');
    const [uploadHouseId, setUploadHouseId] = useState('');
    const [uploadRoomId, setUploadRoomId] = useState('');
    const [uploadScale, setUploadScale] = useState(1);
    const [uploading, setUploading] = useState(false);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterFormat, setFilterFormat] = useState('');

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 50,
        total: 0,
        pages: 0,
        has_next: false,
        has_prev: false
    });

    // Storage usage state
    const [storageUsage, setStorageUsage] = useState({
        used_mb: 0,
        quota_mb: 1024,
        percentage: 0,
        model_count: 0
    });

    const fetchModels = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const response = await Model3DService.getModels({
                page,
                per_page: 50,
                category: filterCategory || undefined,
                format: filterFormat || undefined
            });
            setModels(response.models || []);
            setPagination(response.pagination || {
                page: 1,
                per_page: 50,
                total: 0,
                pages: 0,
                has_next: false,
                has_prev: false
            });
            setError(null);
        } catch (err) {
            console.error('Failed to fetch 3D models:', err);
            setError('Failed to load 3D models');
        } finally {
            setLoading(false);
        }
    }, [filterCategory, filterFormat]);

    const fetchHouses = useCallback(async () => {
        try {
            const data = await HouseService.getHouses();
            setHouses(data);
        } catch (err) {
            console.error('Failed to fetch houses:', err);
        }
    }, []);

    const fetchRooms = useCallback(async () => {
        try {
            const { rooms: fetched } = await RoomService.getRooms({ limit: 100, sort: 'name' });
            setRooms(fetched || []);
        } catch (err) {
            console.error('Failed to fetch rooms:', err);
        }
    }, []);

    const fetchStorageUsage = useCallback(async () => {
        try {
            const data = await Model3DService.getStorageUsage();
            setStorageUsage(data);
        } catch (err) {
            console.error('Failed to fetch storage usage:', err);
        }
    }, []);

    useEffect(() => {
        fetchModels(1);  // Reset to page 1 when filters change
    }, [fetchModels]);

    useEffect(() => {
        fetchHouses();
        fetchRooms();
        fetchStorageUsage();
    }, [fetchHouses, fetchRooms, fetchStorageUsage]);

    // Pagination handlers
    const handleNextPage = () => {
        if (pagination.has_next) {
            fetchModels(pagination.page + 1);
        }
    };

    const handlePrevPage = () => {
        if (pagination.has_prev) {
            fetchModels(pagination.page - 1);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            fetchModels(page);
        }
    };



    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!Model3DService.isSupported(file.name)) {
                toast.error('Unsupported file format. Allowed: GLB, GLTF, OBJ, FBX, USDZ, Splat, PLY');
                return;
            }
            if (file.size > 100 * 1024 * 1024) {
                toast.error('File size must be less than 100MB');
                return;
            }
            setUploadFile(file);
            if (!uploadName) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setUploadName(nameWithoutExt);
            }
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) {
            toast.error('Please select a file');
            return;
        }

        setUploading(true);
        try {
            await Model3DService.uploadModel(uploadFile, {
                name: uploadName || uploadFile.name,
                description: uploadDescription,
                category: uploadCategory,
                house_id: uploadHouseId || null,
                room_id: uploadRoomId || null,
                default_scale: uploadScale
            });

            toast.success('3D model uploaded successfully');
            setShowUploadModal(false);
            resetUploadForm();
            fetchModels();
            fetchStorageUsage();
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error(err.response?.data?.error || 'Failed to upload 3D model');
        } finally {
            setUploading(false);
        }
    };

    const resetUploadForm = () => {
        setUploadFile(null);
        setUploadName('');
        setUploadDescription('');
        setUploadCategory('other');
        setUploadHouseId('');
        setUploadRoomId('');
        setUploadScale(1);
    };

    /**
     * Handles the view action for a model.
     * Fetches the model blob via the service and sets up the viewer.
     * Uses the useBlobUrl hook to manage the blob URL lifecycle.
     * 
     * @param {Object} model - The model object to view
     */
    const handleView = async (model) => {
        // We set selectedModel so we know WHO we are trying to load for the spinner
        setSelectedModel(model);

        try {
            // Fetch file through backend proxy and create blob URL
            // This avoids CORS issues with R2 and handles authentication
            await loadUrl(() => Model3DService.getFileBlobUrl(model.id));
            setShowViewerModal(true);
        } catch (err) {
            console.error('Failed to load model:', err);
            toast.error('Failed to load 3D model');
            setSelectedModel(null);
        }
    };

    const handleDownload = async (model) => {
        try {
            const { url, filename } = await Model3DService.getDownloadUrl(model.id);

            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Failed to get download URL:', err);
            toast.error('Failed to download model');
        }
    };

    const closeViewerModal = () => {
        setShowViewerModal(false);
        setUrl(null); // Triggers cleanup in hook
        setSelectedModel(null);
    };

    const handleEdit = (model) => {
        setSelectedModel(model);
        setShowEditModal(true);
    };

    const handleEditSave = async (updates) => {
        try {
            await Model3DService.updateModel(selectedModel.id, updates);
            toast.success('Model updated');
            setShowEditModal(false);
            setSelectedModel(null);
            fetchModels();
        } catch (err) {
            console.error('Update failed:', err);
            toast.error('Failed to update model');
        }
    };

    const handleDelete = (model) => {
        setSelectedModel(model);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await Model3DService.deleteModel(selectedModel.id);
            toast.success('Model deleted');
            fetchModels();
            fetchStorageUsage();
        } catch (err) {
            console.error('Delete failed:', err);
            toast.error('Failed to delete model');
        }
        setSelectedModel(null);
    };

    // Client-side search filter (category and format are handled server-side)
    const filteredModels = models.filter(model => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            model.name?.toLowerCase().includes(query) ||
            model.original_filename?.toLowerCase().includes(query)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-main)] theme-transition">3D Models</h1>
                    <p className="text-[var(--text-secondary)] theme-transition">
                        Upload and manage 3D models for your scene viewer
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Storage Usage Meter */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg">
                        <HardDrive size={18} className="text-[var(--text-muted)]" />
                        <div className="flex flex-col min-w-[120px]">
                            <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                                <span>Storage</span>
                                <span>{storageUsage.used_mb.toFixed(1)}MB / {storageUsage.quota_mb >= 1024 ? `${(storageUsage.quota_mb / 1024).toFixed(1)}GB` : `${storageUsage.quota_mb}MB`}</span>
                            </div>
                            <div className="w-full h-1.5 bg-[var(--bg-hover)] rounded-full overflow-hidden mt-1">
                                <div
                                    className={`h-full rounded-full transition-all ${storageUsage.percentage >= 90 ? 'bg-red-500' :
                                        storageUsage.percentage >= 70 ? 'bg-yellow-500' :
                                            'bg-[var(--primary)]'
                                        }`}
                                    style={{ width: `${Math.min(storageUsage.percentage, 100)}%` }}
                                />
                            </div>
                            <span className="text-xs text-[var(--text-muted)] mt-0.5">
                                {storageUsage.percentage.toFixed(0)}% used • {storageUsage.model_count} models
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                    >
                        <Upload size={18} />
                        Upload Model
                    </button>
                </div>
            </div>

            {/* Filters */}
            <Card className="p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" />
                        <input
                            type="text"
                            placeholder="Search models..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-[var(--text-muted)]" />
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                        >
                            <option value="">All Categories</option>
                            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <select
                            value={filterFormat}
                            onChange={(e) => setFilterFormat(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                        >
                            <option value="">All Formats</option>
                            <option value="glb">GLB</option>
                            <option value="gltf">GLTF</option>
                            <option value="obj">OBJ</option>
                            <option value="fbx">FBX</option>
                            <option value="usdz">USDZ</option>
                            <option value="splat">Splat</option>
                            <option value="ply">PLY</option>
                        </select>
                    </div>
                </div>
            </Card>

            {/* Error State */}
            {error && (
                <Card className="p-6 text-center text-red-500 mb-6">
                    {error}
                </Card>
            )}

            {/* Empty State */}
            {!error && filteredModels.length === 0 && (
                <Card className="p-8 text-center">
                    <div className="w-16 h-16 bg-[var(--bg-hover)] rounded-full flex items-center justify-center mx-auto mb-6 theme-transition">
                        <Box size={32} className="text-[var(--primary)]" />
                    </div>
                    <h2 className="text-xl font-bold text-[var(--text-main)] mb-2 theme-transition">
                        {searchQuery || filterCategory || filterFormat ? 'No matching models' : 'No 3D models yet'}
                    </h2>
                    <p className="text-[var(--text-secondary)] mb-6 theme-transition">
                        {searchQuery || filterCategory || filterFormat
                            ? 'Try adjusting your search or filters'
                            : 'Upload your first 3D model to get started'}
                    </p>
                    {!searchQuery && !filterCategory && !filterFormat && (
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                        >
                            <Upload size={18} />
                            Upload Model
                        </button>
                    )}
                </Card>
            )}

            {/* Model Grid */}
            {filteredModels.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredModels.map((model) => (
                        <Card key={model.id} className="p-4 hover:shadow-lg transition-shadow">
                            {/* Model Preview (placeholder) */}
                            <div
                                className={`aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-4 flex items-center justify-center cursor-pointer hover:from-indigo-50 hover:to-indigo-100 transition-colors ${viewerLoading ? 'opacity-50 pointer-events-none' : ''}`}
                                onClick={() => !viewerLoading && handleView(model)}
                            >
                                <div className="text-center">
                                    {viewerLoading && selectedModel?.id === model.id ? (
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                                    ) : (
                                        <Box size={48} className="mx-auto mb-2 text-gray-400" />
                                    )}
                                    <span className="text-sm text-gray-500">
                                        {viewerLoading && selectedModel?.id === model.id ? 'Loading...' : 'Click to view'}
                                    </span>
                                </div>
                            </div>

                            {/* Model Info */}
                            <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                    <h3 className="font-medium text-[var(--text-main)] truncate flex-1 theme-transition">
                                        {model.name}
                                    </h3>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${FORMAT_COLORS[model.format] || 'bg-gray-100 text-gray-700'}`}>
                                        {model.format?.toUpperCase()}
                                    </span>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)]">
                                    <span className="px-2 py-0.5 bg-[var(--bg-hover)] rounded text-xs">
                                        {CATEGORY_LABELS[model.category] || model.category}
                                    </span>
                                    <span>{Model3DService.formatFileSize(model.file_size)}</span>
                                </div>

                                {model.description && (
                                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2 theme-transition">
                                        {model.description}
                                    </p>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-1 pt-2 border-t border-[var(--border)]">
                                    <button
                                        onClick={() => handleView(model)}
                                        className="flex-1 flex items-center justify-center gap-1 p-2 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="View in 3D"
                                    >
                                        <Eye size={16} />
                                        <span className="text-sm">View</span>
                                    </button>
                                    <button
                                        onClick={() => handleDownload(model)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Download"
                                    >
                                        <Download size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(model)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Edit"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(model)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-red-500 hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                        onClick={handlePrevPage}
                        disabled={!pagination.has_prev}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        <ChevronLeft size={16} />
                        Prev
                    </button>

                    <div className="flex items-center gap-1">
                        {/* Show first page */}
                        {pagination.page > 2 && (
                            <>
                                <button
                                    onClick={() => handlePageChange(1)}
                                    className="w-8 h-8 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition"
                                >
                                    1
                                </button>
                                {pagination.page > 3 && (
                                    <span className="text-[var(--text-muted)]">...</span>
                                )}
                            </>
                        )}

                        {/* Show surrounding pages */}
                        {[pagination.page - 1, pagination.page, pagination.page + 1]
                            .filter(p => p >= 1 && p <= pagination.pages)
                            .map(page => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`w-8 h-8 rounded-lg transition ${page === pagination.page
                                        ? 'bg-[var(--primary)] text-white'
                                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))
                        }

                        {/* Show last page */}
                        {pagination.page < pagination.pages - 1 && (
                            <>
                                {pagination.page < pagination.pages - 2 && (
                                    <span className="text-[var(--text-muted)]">...</span>
                                )}
                                <button
                                    onClick={() => handlePageChange(pagination.pages)}
                                    className="w-8 h-8 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition"
                                >
                                    {pagination.pages}
                                </button>
                            </>
                        )}
                    </div>

                    <button
                        onClick={handleNextPage}
                        disabled={!pagination.has_next}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        Next
                        <ChevronRight size={16} />
                    </button>

                    <span className="ml-4 text-sm text-[var(--text-muted)]">
                        {pagination.total} total models
                    </span>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="upload-3d-model-modal-title"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
                >
                    <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl theme-transition max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-card)]">
                            <h2 id="upload-3d-model-modal-title" className="text-lg font-bold text-[var(--text-main)] theme-transition">Upload 3D Model</h2>
                            <button
                                type="button"
                                aria-label="Close dialog"
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] transition"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* File Drop Zone */}
                            <div
                                className={`border-2 border-dashed rounded-lg p-6 text-center transition ${uploadFile
                                    ? 'border-[var(--primary)] bg-[var(--primary-bg)]'
                                    : 'border-[var(--border)] hover:border-[var(--primary)]'
                                    }`}
                            >
                                <input
                                    type="file"
                                    accept=".glb,.gltf,.obj,.fbx,.usdz,.splat,.ply"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="model-upload"
                                />
                                <label htmlFor="model-upload" className="cursor-pointer">
                                    {uploadFile ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <Box size={24} className="text-[var(--primary)]" />
                                            <span className="text-[var(--text-main)] font-medium">{uploadFile.name}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <Upload size={32} className="mx-auto mb-2 text-[var(--text-muted)]" />
                                            <p className="text-[var(--text-main)] font-medium">Click to upload 3D model</p>
                                            <p className="text-sm text-[var(--text-muted)]">GLB, GLTF, OBJ, FBX, USDZ, Splat, PLY</p>
                                            <p className="text-xs text-[var(--text-muted)] mt-1">Max file size: 100MB</p>
                                        </>
                                    )}
                                </label>
                            </div>

                            <InputGroup label="Model Name">
                                <Input
                                    value={uploadName}
                                    onChange={(e) => setUploadName(e.target.value)}
                                    placeholder="Enter model name"
                                />
                            </InputGroup>

                            <InputGroup label="Category">
                                <Select
                                    value={uploadCategory}
                                    onChange={(e) => setUploadCategory(e.target.value)}
                                >
                                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </Select>
                            </InputGroup>

                            <InputGroup label="Default Scale">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="range"
                                        min="0.1"
                                        max="10"
                                        step="0.1"
                                        value={uploadScale}
                                        onChange={(e) => setUploadScale(parseFloat(e.target.value))}
                                        className="flex-1"
                                    />
                                    <span className="w-12 text-center text-[var(--text-main)]">{uploadScale}x</span>
                                </div>
                            </InputGroup>

                            {houses.length > 0 && (
                                <InputGroup label="Associate with House (Optional)">
                                    <Select
                                        value={uploadHouseId}
                                        onChange={(e) => setUploadHouseId(e.target.value)}
                                    >
                                        <option value="">No house selected</option>
                                        {houses.map((house) => (
                                            <option key={house.id} value={house.id}>{house.name}</option>
                                        ))}
                                    </Select>
                                </InputGroup>
                            )}

                            {rooms.length > 0 && (
                                <InputGroup label="Associate with Room (Optional)">
                                    <Select
                                        value={uploadRoomId}
                                        onChange={(e) => setUploadRoomId(e.target.value)}
                                    >
                                        <option value="">No room selected</option>
                                        {rooms.map((room) => (
                                            <option key={room.id} value={room.id}>{room.name}</option>
                                        ))}
                                    </Select>
                                </InputGroup>
                            )}

                            <InputGroup label="Description (Optional)">
                                <textarea
                                    value={uploadDescription}
                                    onChange={(e) => setUploadDescription(e.target.value)}
                                    placeholder="Add a description..."
                                    rows={3}
                                    className="w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none resize-none"
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-3 p-4 border-t border-[var(--border)] sticky bottom-0 bg-[var(--bg-card)]">
                            <button
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={!uploadFile || uploading}
                                className="flex-1 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3D Viewer Modal */}
            {showViewerModal && selectedModel && viewerUrl && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="viewer-3d-modal-title"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
                >
                    <div className="bg-[var(--bg-card)] w-full max-w-4xl h-[80vh] rounded-2xl shadow-xl theme-transition flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
                            <div>
                                <h2 id="viewer-3d-modal-title" className="text-lg font-bold text-[var(--text-main)] theme-transition">{selectedModel.name}</h2>
                                <p className="text-sm text-[var(--text-secondary)]">{selectedModel.format?.toUpperCase()} • {Model3DService.formatFileSize(selectedModel.file_size)}</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeViewerModal}
                                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                aria-label="Close dialog"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 relative">
                            <ErrorBoundary>
                                <Model3DViewer
                                    url={viewerUrl}
                                    format={selectedModel.format}
                                    scale={selectedModel.default_scale}
                                    className="w-full h-full"
                                    onError={() => toast.error('Error displaying 3D model')}
                                />
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedModel && (
                <EditModelModal
                    model={selectedModel}
                    houses={houses}
                    rooms={rooms}
                    onClose={() => { setShowEditModal(false); setSelectedModel(null); }}
                    onSave={handleEditSave}
                />
            )}

            {/* Delete Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => { setShowDeleteConfirm(false); setSelectedModel(null); }}
                onConfirm={confirmDelete}
                title="Delete 3D Model"
                message={`Are you sure you want to delete "${selectedModel?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                isDanger={true}
            />
        </div>
    );
};

// Edit Model Modal Component
const EditModelModal = ({ model, houses, rooms, onClose, onSave }) => {
    const [name, setName] = useState(model.name || '');
    const [description, setDescription] = useState(model.description || '');
    const [category, setCategory] = useState(model.category || 'other');
    const [houseId, setHouseId] = useState(model.house_id || '');
    const [roomId, setRoomId] = useState(model.room_id || '');
    const [defaultScale, setDefaultScale] = useState(model.default_scale || 1);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await onSave({
            name,
            description,
            category,
            house_id: houseId || null,
            room_id: roomId || null,
            default_scale: defaultScale
        });
        setSaving(false);
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-3d-model-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
        >
            <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl theme-transition max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
                    <h2 id="edit-3d-model-modal-title" className="text-lg font-bold text-[var(--text-main)] theme-transition">Edit Model</h2>
                    <button
                        type="button"
                        aria-label="Close dialog"
                        onClick={onClose}
                        className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <InputGroup label="Model Name">
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter model name"
                        />
                    </InputGroup>

                    <InputGroup label="Category">
                        <Select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </Select>
                    </InputGroup>

                    <InputGroup label="Default Scale">
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min="0.1"
                                max="10"
                                step="0.1"
                                value={defaultScale}
                                onChange={(e) => setDefaultScale(parseFloat(e.target.value))}
                                className="flex-1"
                            />
                            <span className="w-12 text-center text-[var(--text-main)]">{defaultScale}x</span>
                        </div>
                    </InputGroup>

                    {houses.length > 0 && (
                        <InputGroup label="Associate with House">
                            <Select
                                value={houseId}
                                onChange={(e) => setHouseId(e.target.value)}
                            >
                                <option value="">No house selected</option>
                                {houses.map((house) => (
                                    <option key={house.id} value={house.id}>{house.name}</option>
                                ))}
                            </Select>
                        </InputGroup>
                    )}

                    {rooms.length > 0 && (
                        <InputGroup label="Associate with Room">
                            <Select
                                value={roomId}
                                onChange={(e) => setRoomId(e.target.value)}
                            >
                                <option value="">No room selected</option>
                                {rooms.map((room) => (
                                    <option key={room.id} value={room.id}>{room.name}</option>
                                ))}
                            </Select>
                        </InputGroup>
                    )}

                    <InputGroup label="Description">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add a description..."
                            rows={3}
                            className="w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none resize-none"
                        />
                    </InputGroup>
                </div>

                <div className="flex gap-3 p-4 border-t border-[var(--border)]">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Models3D;
